import { ActionTypes as ApplicationLandingAction } from '../actions/applicationLandingActions';
import { ActionTypes as InitialDetailsActions } from '../actions/initialDetailsActions';
import { ActionTypes as IdentificationActions } from '../models/actions/identification';
import { ActionTypes as PaymentDetailsActions } from '../actions/paymentDetailsActions';
import { VerificationActionTypes as VerificationActions } from '../models/actions/verification';
import { ActionTypes as CommonActions } from '../models/actions/common';
import IErrorState from '../models/IErrorState';

const phoneNumber = '855-678-6248';

export type ErrorActionTypes =
  | ApplicationLandingAction
  | IdentificationActions
  | InitialDetailsActions
  | PaymentDetailsActions
  | CommonActions
  | VerificationActions;

export const ERROR_CODES = {
  // ApplicationLandingActionInit error codes
  noToken: 'noToken',

  // InitialDetailsActions error codes
  downForMaintenance: 'downForMaintenance',
  applicationNotFound: 'applicationNotFound',
  customerNotPermitted: 'customerNotPermitted',
  applicationLocked: 'applicationLocked',
  customerNotFound: 'customerNotFound',
  DeniedState: 'DeniedState',
  applicationBlockedIneligibleState: 'applicationBlockedIneligibleState',

  // IdentificationActions error codes
  loginFailed: 'loginFailed',
  pinCodeIsMandatory: 'pinCodeIsMandatory',
  decline: 'decline',
  ofac_decline: 'ofac_decline',
  credit_line_processing_failed: 'credit_line_processing_failed',
  consumer_alert: 'consumer_alert',
  fullSsnLoginRequired: 'fullSsnLoginRequired',
  ssnIsMandatory: 'ssnIsMandatory',
  IncorrectDateOfBirth: 'IncorrectDateOfBirth',

  // PaymentDetailsActions error codes
  existingPaymentMethodSelectionMissing: 'existingPaymentMethodSelectionMissing',
  dueDateIsMissing: 'dueDateIsMissing',
  applicationBlocked: 'applicationBlocked',
  applicationBlockedPaymentMethod: 'applicationBlockedPaymentMethod',
  applicationBlockedRisk: 'applicationBlockedRisk',
  applicationBlockedConsumerAlert: 'applicationBlockedConsumerAlert',
  applicationBlockedDeprecated: 'applicationBlockedDeprecated',
  cardApplicationFail: 'cardApplicationFail',
  applicationBlockedTerminal: 'applicationBlockedTerminal',
  paymentProfileBlocked: 'paymentProfileBlocked',
  TransactionDeclined: 'TransactionDeclined',
  WrongCardType: 'WrongCardType',
  PaymentProfileError: 'PaymentProfileError',
  cardApplicationVerification: 'cardApplicationVerification',
  InvalidPaymentMethod : 'InvalidPaymentMethod'
};

export const errors: Partial<{
  [index in ErrorActionTypes]: { [index: string]: IErrorState & { submitActionType?: ErrorActionTypes } };
}> = {
  [ApplicationLandingAction.Init]: {
    [ERROR_CODES.noToken]: {
      isModal: false,
      isPage: true,
      description: 'The Sunbit Card application can only be accessed by clicking on a personalized invitation link.',
    },
  },
  [InitialDetailsActions.getInitialDetails]: {
    [ERROR_CODES.downForMaintenance]: {
      isModal: false,
      isPage: true,
      description: `<div>The Sunbit Card Application is currently down for maintenance.</div>
      <div>Don’t worry, we will be back soon.</div>
      </br>
      <div>We look forward to seeing you.</div>
      <div>Thank you for your patience.</div>
</br>
      <a href="https://sunbit.com/sunbit-card/">Learn more about the Sunbit Card »</a>
`,
    },
    [ERROR_CODES.applicationLocked]: {
      isModal: false,
      isPage: true,
      description: `We're sorry, but your link has expired. Please contact us at 855-678-6248.`,
    },
    [ERROR_CODES.applicationBlockedTerminal]: {
      isModal: false,
      isPage: true,
      description: `We're sorry, but your link has expired. Please contact us at 855-678-6248.`,
    },
    [ERROR_CODES.applicationBlocked]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, due to multiple unsuccessful attempts your application cannot be approved at this time. You will receive a notice via email with additional details.',
    },
    [ERROR_CODES.applicationBlockedIneligibleState]: {
      isModal: false,
      isPage: true,
      description: `You have already applied for the Sunbit Card but unfortunately, your application was not approved. We’ve sent you a notice via email with additional details.`,
    },
    [ERROR_CODES.applicationBlockedDeprecated]: {
      isModal: false,
      isPage: true,
      description: `We're sorry, but your link has expired. Please contact us at 855-678-6248.`,
    },
    [ERROR_CODES.applicationBlockedPaymentMethod]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, due to multiple unsuccessful attempts your application cannot be approved at this time. You will receive a notice via email with additional details.',
    },
    [ERROR_CODES.applicationBlockedConsumerAlert]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES.applicationBlockedRisk]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
  },

  [IdentificationActions.Identify]: {
    [ERROR_CODES.loginFailed]: {
      isModal: false,
      isPage: false,
    },
    [ERROR_CODES.pinCodeIsMandatory]: {
      isModal: false,
      isPage: false,
    },
    [ERROR_CODES.applicationLocked]: {
      isModal: false,
      isPage: true,
      description: `Due to multiple unsuccessful attempts, your application cannot be approved at this time. Please contact us at ${phoneNumber}.`,
    },
    [ERROR_CODES.decline]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES['ofac_decline']]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES['credit_line_processing_failed']]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES['consumer_alert']]: {
      isModal: true,
      isPage: false,
      description: `Due to an alert on your consumer report, we are required to verify your identity before proceeding. Please contact us at ${phoneNumber} to proceed with your application.`,
      buttonTitle: 'OK',
      submitActionType: CommonActions.continueExistingApplicationWithConsumerAlert,
    },
    [ERROR_CODES['fullSsnLoginRequired']]: {
      isModal: false,
      isPage: false,
    },
    [ERROR_CODES['ssnIsMandatory']]: {
      isModal: false,
      isPage: false,
    },
    [ERROR_CODES['cardApplicationVerification']]: {
      isModal: false,
      isPage: false,
      submitActionType: VerificationActions.getVerifyLink,
    },
  },
  [IdentificationActions.SettingAddress]: {
    [ERROR_CODES.DeniedState]: {
      isModal: false,
      isPage: true,
      description: ({ state }: { state: string }) =>
        `At this time, residents of ${state} are not eligible to apply for the Sunbit Card. Please contact us at ${phoneNumber}.`,
    },
  },
  [IdentificationActions.ApplyForCreditLine]: {
    [ERROR_CODES.decline]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES['ofac_decline']]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES['credit_line_processing_failed']]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
  },

  [PaymentDetailsActions.SubmitExistingPaymentMethod]: {
    [ERROR_CODES.TransactionDeclined]: {
      isModal: true,
      isPage: false,
      description:
        'Unfortunately, we are unable to validate your card. Please contact your card issuer for more information.',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.PaymentProfileError]: {
      isModal: true,
      isPage: false,
      description: 'Technical error has occurred. Please try again later.',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.WrongCardType]: {
      isModal: true,
      isPage: false,
      description: 'This card type is not accepted. Please use a valid bank-issued debit card',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.applicationBlocked]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, due to multiple unsuccessful attempts your application cannot be approved at this time. You will receive a notice via email with additional details.',
    },
    [ERROR_CODES.applicationBlockedDeprecated]: {
      isModal: false,
      isPage: true,
      description: `We're sorry, but your link has expired. Please contact us at 855-678-6248.`,
    },
    [ERROR_CODES.applicationBlockedPaymentMethod]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, due to multiple unsuccessful attempts your application cannot be approved at this time. You will receive a notice via email with additional details.',
    },
    [ERROR_CODES.applicationBlockedConsumerAlert]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES.applicationBlockedRisk]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES['cardApplicationVerification']]: {
      isModal: false,
      isPage: false,
      submitActionType: VerificationActions.getVerifyLink,
    },
  },
  [PaymentDetailsActions.SubmitNewPaymentMethod]: {
    [ERROR_CODES.TransactionDeclined]: {
      isModal: true,
      isPage: false,
      description:
        'Unfortunately, we are unable to validate your card. Please contact your card issuer for more information.',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.PaymentProfileError]: {
      isModal: true,
      isPage: false,
      description: 'Technical error has occurred. Please try again later.',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.InvalidPaymentMethod]: {
      isModal: true,
      isPage: false,
      title: 'This payment method is not valid.',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.WrongCardType]: {
      isModal: true,
      isPage: false,
      description: 'This card type is not accepted. Please use a valid bank-issued debit card',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.PaymentProfileError]: {
      isModal: true,
      isPage: false,
      description: 'Technical error has occurred. Please try again later.',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.WrongCardType]: {
      isModal: true,
      isPage: false,
      description: 'This card type is not accepted. Please use a valid bank-issued debit card',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.applicationBlocked]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, due to multiple unsuccessful attempts your application cannot be approved at this time. You will receive a notice via email with additional details.',
    },
    [ERROR_CODES.cardApplicationFail]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, your application cannot be approved at this time. You will receive an email with more information',
    },
    [ERROR_CODES.applicationBlockedDeprecated]: {
      isModal: false,
      isPage: true,
      description: `We're sorry, but your link has expired. Please contact us at 855-678-6248.`,
    },
    [ERROR_CODES.applicationBlockedPaymentMethod]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, due to multiple unsuccessful attempts your application cannot be approved at this time. You will receive a notice via email with additional details.',
    },
    [ERROR_CODES.applicationBlockedConsumerAlert]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES.applicationBlockedRisk]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES.cardApplicationVerification]: {
      isModal: false,
      isPage: false,
      submitActionType: VerificationActions.getVerifyLink,
    },
  },
  [PaymentDetailsActions.SubmitNewPaymentMethodRCC]: {
    [ERROR_CODES.PaymentProfileError]: {
      isModal: true,
      isPage: false,
      title: 'Invalid Account Details',
      description: 'Please try again.',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
    },
    [ERROR_CODES.InvalidPaymentMethod]: {
      isModal: true,
      isPage: false,
      title:'This payment method is not valid.',
      buttonTitle: 'Dismiss',
      submitActionType: CommonActions.ClearState,
  },
    [ERROR_CODES.applicationBlocked]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, due to multiple unsuccessful attempts your application cannot be approved at this time. You will receive a notice via email with additional details.',
    },
    [ERROR_CODES.applicationBlockedDeprecated]: {
      isModal: false,
      isPage: true,
      description: `We're sorry, but your link has expired. Please contact us at 855-678-6248.`,
    },
    [ERROR_CODES.applicationBlockedPaymentMethod]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, due to multiple unsuccessful attempts your application cannot be approved at this time. You will receive a notice via email with additional details.',
    },
    [ERROR_CODES.applicationBlockedConsumerAlert]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES.applicationBlockedRisk]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we are unable to approve your application at this time. You will receive a detailed notice via email with additional details.`,
    },
    [ERROR_CODES.paymentProfileBlocked]: {
      isModal: false,
      isPage: true,
      description: `Unfortunately, we were unable to verify your deposit account details for the RCC payment. You will receive further communication via email. If you have any questions, please call us at 855.678.6248.`,
    },
    [ERROR_CODES.cardApplicationVerification]: {
      isModal: false,
      isPage: false,
      submitActionType: VerificationActions.getVerifyLink,
    },
  },
  [VerificationActions.manualFailure]: {
    [ERROR_CODES.cardApplicationFail]: {
      isModal: false,
      isPage: true,
      description:
        'Unfortunately, your application cannot be approved at this time. You will receive an email with more information',
    },
  },
};

export const fallbackError = {
  isModal: false,
  isPage: true,
  description: `Unfortunately, we are unable to process your application at this time. Please contact customer support at ${phoneNumber}.`,
};
